import { useState, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { exportHistoricoEncuesta } from '../../../domain/api/AlcoholimetriaApi';
import { getDateRange } from '../utils/formatters';
import { concatenateStationIds } from '../components/filters/utils/stationUtils';

/**
 * Custom hook para exportar el histórico de encuestas como archivo Excel
 * Descarga un archivo .xls desde la API usando requestBlob
 * 
 * @returns {Object} { exportar, loading, error }
 * @returns {Function} return.exportar - Función para ejecutar la descarga
 * @returns {boolean} return.loading - Estado de descarga en progreso
 * @returns {string|null} return.error - Mensaje de error si ocurre
 */
export const useExportHistorico = () => {
    const dispatch = useDispatch();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    /**
     * Ejecuta la descarga del archivo Excel
     * @param {Object} params
     * @param {string|number|Array} params.stationId - ID de estación(es) EVBI. Puede ser un ID único o array de IDs
     * @param {number} params.month - Mes (1-12)
     * @param {number} params.year - Año
     */
    const exportar = useCallback(async ({stationName , stationId, month, year, filterEmpresa = '', filterArea = '', filterCargo = ''}) => {
        if (!stationId) {
            setError('Debe seleccionar una estación');
            return;
        }

        if (!month || !year) {
            setError('Debe seleccionar mes y año');
            return;
        }

        const { datestart, dateend } = getDateRange(month, year);

        if (!datestart || !dateend) {
            setError('Error al calcular el rango de fechas');
            return;
        }

        try {
            setLoading(true);
            setError(null);

            // Concatenar estaciones con "-" si hay múltiples
            const concatenatedStationId = concatenateStationIds(stationId);

            const blob = await exportHistoricoEncuesta(dispatch, concatenatedStationId, datestart, dateend, filterEmpresa, filterArea, filterCargo);

            if (!blob || blob.error) {
                setError('Error al descargar el archivo');
                return;
            }

            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `reporte_alcoholimetria_${stationName}_${datestart}_${dateend}.xls`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Error exporting histórico encuesta:', err);
            setError('Error al descargar el archivo');
        } finally {
            setLoading(false);
        }
    }, [dispatch]);

    return { exportar, loading, error };
};

export default useExportHistorico;
